import type { QueryClient } from '@tanstack/react-query';

import { createQueryClient } from '@/shared/api/queryClient';
import { getValidAccessToken, startTokenAutoRefresh } from '@/shared/auth/token';
import { startCrossTabSync } from '@/shared/auth/crossTab';
import { bootstrapSession } from '@/shared/auth/session';
import { startPreferenceSync } from '@/shared/auth/preferences';
import {
  ensureLanguage,
  getLanguage,
  initI18n,
  registerLanguageLoader,
  sharedResources,
  type NamespaceResources,
} from '@/shared/i18n/i18n';
import { installFileDropGuard } from '@/shared/lib/dropGuard';
import {
  isPresencePayload,
  PRESENCE_CHANGED,
  REALTIME_RECONNECTED,
  RealtimeManager,
  usePresenceStore,
} from '@/shared/realtime';
import { ModuleRegistry } from '@/shared/registry/registry';
import { startThemeController } from '@/shared/theme/store';

import en from './locales/en.json';
import { loadModuleLocales } from './locales/moduleLocales';
import ru from './locales/ru.json';
import uk from './locales/uk.json';
import { discoverModules } from './modules';
import { bindSessionLifecycle } from './sessionLifecycle';

export const APP_NAMESPACE = 'app';
export const appResources: NamespaceResources = { namespace: APP_NAMESPACE, resources: { ru, en, uk } };

export interface AppRuntime {
  registry: ModuleRegistry;
  queryClient: QueryClient;
  realtime: RealtimeManager;
  /** Resolves when the texts of the initial language are loaded (render after it). */
  ready: Promise<void>;
}

export interface RuntimeOptions {
  /** Realtime needs the API's SignalR hub; disabled with the mock API. */
  realtimeEnabled: boolean;
}

/**
 * Wires the application services once at startup: i18n with every namespace, theme, token refresh,
 * cross-tab auth sync, realtime lifecycle bound to the auth status, and module `setup` hooks.
 */
export function createRuntime({ realtimeEnabled }: RuntimeOptions): AppRuntime {
  const modules = discoverModules();
  const registry = new ModuleRegistry(modules);

  // The shell's own texts are in the main bundle; the modules' come per language from one chunk each.
  const i18n = initI18n([
    sharedResources,
    appResources,
    ...modules.flatMap((m) => (m.locales ? [{ namespace: m.id, resources: m.locales }] : [])),
  ]);
  registerLanguageLoader(loadModuleLocales);
  const ready = ensureLanguage(getLanguage());

  const queryClient = createQueryClient();
  const realtime = new RealtimeManager({ accessTokenFactory: getValidAccessToken });

  realtime.on(PRESENCE_CHANGED, (payload) => {
    if (isPresencePayload(payload)) usePresenceStore.getState().setOnline(payload.userId, payload.isOnline);
  });
  // Events may have been missed while disconnected: drop live presence (it would override the fresh API data with
  // dots from before the gap) and refetch what is on screen.
  realtime.on(REALTIME_RECONNECTED, () => {
    usePresenceStore.getState().reset();
    void queryClient.invalidateQueries({ type: 'active' });
  });

  startThemeController();
  installFileDropGuard();
  startTokenAutoRefresh();
  startCrossTabSync();
  startPreferenceSync();

  bindSessionLifecycle({ queryClient, realtime, realtimeEnabled });

  for (const module of modules) module.setup?.({ queryClient, realtime, i18n });

  void bootstrapSession();
  return { registry, queryClient, realtime, ready };
}
